import { FC, Fragment, useContext, useMemo } from "react";
import { ZipProjectContext } from "@app/projects/zip-projects/ZipProjectContext";
import { ZipCodeTableData, ZipProjectActionsEnum, ZipCodeFilterParams, ZipProjectModuleRoutesEnum } from "@app/projects/zip-projects/interfaces";
import { ColumnConfig, ColumnGroup, SortDirections, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { Link, useLocation, useRouteMatch } from "react-router-dom";
import { RegionGranularity } from "@app/region-sets";
import { NetworkAssessmentAnalysisType, ProjectPerspective } from "@app/projects/enumerators";
import { ExportType } from "@app/exports/enumerations";
import {
  blankZipCodeRow,
  fillInMissingCompareModeRows,
  getTopAndBottomRunConfigurations,
  getSearchParametersWithPrimaryCounty,
  shouldUseComparedZipDetails,
  printNumericString,
  printPercentString,
  getResultsForLabel
} from "@app/projects/zip-projects/utility";
import InformationalIndicator from "@app/projects/network-assessment/InformationalSpecialtyGroupIndicator";
import { CalculationMethodEnum } from "@app/templates/templateModels";
import { getPrimaryAndSecondaryRunConfigurations, getMarketAnalysisTooltip, getZipPrimaryCountyTooltip } from "@app/projects/network-assessment/adequacy/utility";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import { ExportDataRouteState } from "@app/exports/exportsRouteState";
import { ReportCustomizationRouteState } from "@app/report-customization/build-reports/interfaces/reportCustomizationRouteState";
import { ReportType } from "@app/report-customization/build-reports/interfaces/reportTypeEnum";
import { ZipCodeDisplayModel } from "@app/projects/zip-projects/interfaces/zipCodeDisplayModel";
import SpecialtyGroupExceptionTimelineLink from "@app/projects/network-assessment/SpecialtyGroupExceptionTimelineLink";
import { SpecialtyGroupExceptionStatus } from "@app/exceptions/enums";
import { useZipProjectPaginationProps } from "@app/projects/zip-projects/UseZipProjectPaginationProps";
import { formatDateCustom, formatDateTime } from "@app/shared/formatDateTime";
import { QESButton } from "@app/shared/qes-standards/Button";
import useGapFillStatusDescriptions from "@app/projects/network-assessment/adequacy/hooks/useGapFillStatusDescriptions";

interface RouteMatchParams {
  projectId: string;
}

export const ZipCodeTable: FC = () => {
  const { state, dispatch, ...projectContext } = useContext(ZipProjectContext);
  const location = useLocation();
  const routeMatch = useRouteMatch<RouteMatchParams>();
  const { enabledFeatureFlags } = useContext(FeatureFlagContext);
  const {calculatedTotalNumberOfRecords, paginationProps } = useZipProjectPaginationProps();
  const displayMarketAnalysisResults = (enabledFeatureFlags.has(Features.ZIP_PROSPECT_AND_MARKET)
    && (state.runConfiguration.useNetworkProvidersForMarketAnalysis || state.runConfiguration.useProspectProvidersForMarketAnalysis || !!state.runConfiguration.marketProviderSetId));
  const isZipGapResolutionStatusEnabled = enabledFeatureFlags.has(Features.ZIP_GAP_RESOLUTION_STATUS);
  // Like county, the column also needs the project's Show Gap Aging Analysis option.
  const isZipGapAgeTrackingEnabled = enabledFeatureFlags.has(Features.ZIP_GAP_AGE_TRACKING) && !!state.project?.includeGapAging;
  const gapFillStatusDescriptionMap = useGapFillStatusDescriptions();

  const handleCheckboxChange = (wafflesSelectedKeys: string[]) => {
    dispatch({
      payload: {
        selectedRows: wafflesSelectedKeys,
      },
      type: ZipProjectActionsEnum.ChangeCheckbox,
    });
  }

  const columnConfig: ColumnConfig = useMemo(() => {
    const ncpCountTwo = state.templateMetadata?.closestProviderCount === 2 || (state.isCompareMode && state.templateMetadataCompared?.closestProviderCount === 2);
    const ncpCountGtOne = state.templateMetadata?.closestProviderCount > 1 || (state.isCompareMode && state.templateMetadataCompared?.closestProviderCount > 1);
    const showDist = state.runConfiguration?.showDistance || (state.isCompareMode && state.compareToRunConfiguration?.showDistance);
    const showTime = state.runConfiguration?.showTime || (state.isCompareMode && state.compareToRunConfiguration?.showTime);
    const isTimeAndDistance = state.calculationMethod?.toLocaleLowerCase() === CalculationMethodEnum.TimeAndDistance.toLocaleLowerCase();
    const isTimeOrDistance = state.calculationMethod?.toLocaleLowerCase() === CalculationMethodEnum.TimeOrDistance.toLocaleLowerCase();
    const showAvgDist = state.project?.showAverageDistance && showDist;
    const showAvgTime = state.project?.showAverageTime && showTime;
    const showMaxDist = state.project?.showMaximumDistance && showDist;
    const showMaxTime = state.project?.showMaximumTime && showTime;
    const showWithTimeAndDistanceColumns = state.runConfiguration.showWithTimeAndDistanceColumns || (state.isCompareMode && state.compareToRunConfiguration?.showWithTimeAndDistanceColumns);
    const hasTelehealth = state.runConfiguration.telehealthSpecialtyGroupCodes.length > 0 || state.compareToRunConfiguration?.telehealthSpecialtyGroupCodes.length > 0;

    return {
      accessTelehealthCredit: {
        displayText: "Telehealth %",
        hidden: !hasTelehealth,
        sortOrder: 427,
        testId: "access-telehealth-credit",
      },
      avgDist: {
        displayText: ncpCountGtOne ? "Avg Dist 1" : "Avg Dist",
        hidden: !showAvgDist,
        sortOrder: 190,
        testId: "avg-dist",
      },
      avgDist2: {
        displayText: "Avg Dist 2",
        hidden: !ncpCountTwo || !showAvgDist,
        sortOrder: 310,
        testId: "avg-dist-2",
      },
      avgDistWith: {
        displayText: ncpCountGtOne ? "Avg Dist 1 With Access" : "Avg Dist With Access",
        hidden: !showAvgDist,
        sortOrder: 200,
        testId: "avg-dist-with",
      },
      avgDistWith2: {
        displayText: "Avg Dist With 2 Access",
        hidden: !ncpCountTwo || !showAvgDist,
        sortOrder: 320,
        testId: "avg-dist-2-with",
      },
      avgDistWithout: {
        displayText: ncpCountGtOne ? "Avg Dist 1 Without Access" : "Avg Dist Without Access",
        hidden: !showAvgDist,
        sortOrder: 210,
        testId: "avg-dist-without",
      },
      avgDistWithout2: {
        displayText: "Avg Dist 2 Without Access",
        hidden: !ncpCountTwo || !showAvgDist,
        sortOrder: 330,
        testId: "avg-dist-2-without",
      },
      avgTime: {
        displayText: ncpCountGtOne ? "Avg Time 1" : "Avg Time",
        hidden: !showAvgTime,
        sortOrder: 250,
        testId: "avg-time",
      },
      avgTime2: {
        displayText: "Avg Time 2",
        hidden: !ncpCountTwo || !showAvgTime,
        sortOrder: 370,
        testId: "avg-time-2",
      },
      avgTimeWith: {
        displayText: ncpCountGtOne ? "Avg Time 1 With Access" : "Avg Time With Access",
        hidden: !showAvgTime,
        sortOrder: 260,
        testId: "avg-time-with",
      },
      avgTimeWith2: {
        displayText: "Avg Time 2 With Access",
        hidden: !ncpCountTwo || !showAvgTime,
        sortOrder: 380,
        testId: "avg-time-2-with",
      },
      avgTimeWithout: {
        displayText: ncpCountGtOne ? "Avg Time 1 Without Access" : "Avg Time Without Access",
        hidden: !showAvgTime,
        sortOrder: 270,
        testId: "avg-time-without",
      },
      avgTimeWithout2: {
        displayText: "Avg Time 2 Without Access",
        hidden: !ncpCountTwo || !showAvgTime,
        sortOrder: 390,
        testId: "avg-time-2-without",
      },
      city: {
        displayText: "City",
        sortOrder: 30,
        testId: "city",
      },
      county: {
        displayText: "Primary County",
        showInfoIcon: true,
        sortOrder: 20,
        testId: "county",
        tooltip: getZipPrimaryCountyTooltip(),
      },
      gapAgeDays: {
        displayText: "Gap Age",
        hidden: !isZipGapAgeTrackingEnabled,
        sortOrder: 140,
        testId: "gap-age-days",
      },
      gapFillStatus: {
        displayText: "Status",
        hidden: !isZipGapResolutionStatusEnabled,
        minWidth: "20em",
        sortOrder: 500,
        testId: "gap-fill-status",
      },
      gapFillStatusUpdatedByName: {
        displayText: "Updated By",
        hidden: !isZipGapResolutionStatusEnabled,
        minWidth: "10em",
        sortOrder: 520,
        testId: "gap-fill-status-updated-by-name",
      },
      gapFillStatusUpdatedDate: {
        displayText: "Last Updated",
        hidden: !isZipGapResolutionStatusEnabled,
        minWidth: "9em",
        sortOrder: 510,
        testId: "gap-fill-status-updated-date",
      },
      inZipMarketProviderCount: {
        displayText: "Market in Zip Code",
        hidden: !displayMarketAnalysisResults,
        sortOrder: 441,
        testId: "in-zip-market-provider-count"
      },
      inZipProviderCount: {
        displayText: "In Zip Code Providers",
        sortOrder: 431,
        testId: "in-zip-provider-count"
      },
      marketAccessPercent: {
        displayText: "Access %",
        hidden: !displayMarketAnalysisResults,
        sortOrder: 435,
        testId: "market-access-percent",
      },
      marketPercent: {
        displayText: "Market %",
        hidden: !displayMarketAnalysisResults,
        sortOrder: 440,
        testId: "market-percent",
      },
      marketServicing: {
        displayText: "Servicing",
        hidden: !displayMarketAnalysisResults,
        sortOrder: 440,
        testId: "market-servicing",
      },
      maxDist: {
        displayText: ncpCountGtOne ? "Max Dist 1" : "Max Dist",
        hidden: !showMaxDist,
        sortOrder: 220,
        testId: "max-dist",
      },
      maxDist2: {
        displayText: "Max Dist 2",
        hidden: !ncpCountTwo || !showMaxDist,
        sortOrder: 340,
        testId: "max-dist-2",
      },
      maxDistWith: {
        displayText: ncpCountGtOne ? "Max Dist 1 With Access" : "Max Dist With Access",
        hidden: !showMaxDist,
        sortOrder: 230,
        testId: "max-dist-with",
      },
      maxDistWith2: {
        displayText: "Max Dist 2 With Access",
        hidden: !ncpCountTwo || !showMaxDist,
        sortOrder: 350,
        testId: "max-dist-2-with",
      },
      maxDistWithout: {
        displayText: ncpCountGtOne ? "Max Dist 1 Without Access" : "Max Dist Without Access",
        hidden: !showMaxDist,
        sortOrder: 240,
        testId: "max-dist-without",
      },
      maxDistWithout2: {
        displayText: "Max Dist 2 Without Access",
        hidden: !ncpCountTwo || !showMaxDist,
        sortOrder: 360,
        testId: "max-dist-2-without",
      },
      maxTime: {
        displayText: ncpCountGtOne ? "Max Time 1" : "Max Time",
        hidden: !showMaxTime,
        sortOrder: 280,
        testId: "max-time",
      },
      maxTime2: {
        displayText: "Max Time 2",
        hidden: !ncpCountTwo || !showMaxTime,
        sortOrder: 400,
        testId: "max-time-2",
      },
      maxTimeWith: {
        displayText: ncpCountGtOne ? "Max Time 1 With Access" : "Max Time With Access",
        hidden: !showMaxTime,
        sortOrder: 290,
        testId: "max-time-with",
      },
      maxTimeWith2: {
        displayText: "Max Time 2 With Access",
        hidden: !ncpCountTwo || !showMaxTime,
        sortOrder: 410,
        testId: "max-time-2-with",
      },
      maxTimeWithout: {
        displayText: ncpCountGtOne ? "Max Time 1 Without Access" : "Max Time Without Access",
        hidden: !showMaxTime,
        sortOrder: 300,
        testId: "max-time-without",
      },
      maxTimeWithout2: {
        displayText: "Max Time 2 Without Access",
        hidden: !ncpCountTwo || !showMaxTime,
        sortOrder: 420,
        testId: "max-time-2-without",
      },
      memberCountWithAccess: {
        displayText: "# With",
        sortOrder: 150,
        testId: "member-count-with",
      },
      memberCountWithoutAccess: {
        displayText: "# Without",
        sortOrder: 170,
        testId: "member-count-without",
      },
      membershipCount: {
        displayText: "Membership Count",
        sortOrder: 66,
        testId: "membership-count",
      },
      membershipType: {
        displayText: "Membership",
        noSort: true,
        sortOrder: 65,
        testId: "membership",
      },
      metDist: {
        displayText: "Met Dist",
        hidden: !showWithTimeAndDistanceColumns || isTimeAndDistance,
        sortOrder: 110,
        testId: "met-dist",
      },
      metPassPercent: {
        displayText: "Met Overall",
        sortOrder: 130,
        testId: "met-pass-percent",
      },
      metTime: {
        displayText: "Met Time",
        hidden: !showWithTimeAndDistanceColumns || isTimeAndDistance,
        sortOrder: 120,
        testId: "met-time",
      },
      minPassPercent: {
        displayText: "Min Pass %",
        sortOrder: 100,
        testId: "min-pass-percent",
      },
      minProviderCount: {
        displayText: "Min Reqd Provs",
        sortOrder: 70,
        testId: "min-provider-count",
      },
      providerCount: {
        displayText: "Servicing Providers",
        sortOrder: 430,
        testId: "provider-count",
      },
      reqDist: {
        displayText: "Reqd Dist",
        hidden: !showDist,
        sortOrder: 80,
        testId: "req-dist",
      },
      reqTime: {
        displayText: "Reqd Time",
        hidden: !showTime,
        sortOrder: 90,
        testId: "req-time",
      },
      resultsFor: {
        displayText: "Results For",
        hidden: !state.isCompareMode,
        noSort: true,
        sortOrder: 63,
        testId: "results-for",
      },
      servicingProviderToMemberRatio: {
        displayText: "Svcg Prov To Mbr Ratio",
        sortOrder: 431,
        testId: "svc-prov-to-mbr-ratio",
      },
      specialty: {
        displayText: "Specialty Group",
        sortOrder: 60,
        testId: "specialty",
      },
      state: {
        displayText: "State",
        sortOrder: 10,
        testId: "state",
      },
      withAccessPercent: {
        displayText: "With %",
        sortOrder: 160,
        testId: "with-percent",
      },
      withDistPercent: {
        displayText: "With Dist %",
        hidden: !isTimeOrDistance,
        sortOrder: ncpCountTwo ? 365 : 245,
        testId: "with-dist-percent",
      },
      withoutAccessPercent: {
        displayText: "Without %",
        sortOrder: 180,
        testId: "without-percent",
      },
      withTimePercent: {
        displayText: "With Time %",
        hidden: !isTimeOrDistance,
        sortOrder: ncpCountTwo ? 425 : 305,
        testId: "with-time-percent",
      },
      zipClass: {
        displayText: "Classification",
        sortOrder: 40,
        testId: "classification",
      },
      zipCode: {
        displayText: "Zip Code",
        sortOrder: 50,
        testId: "zip-code",
      },
    };
  }, [
    state.calculationMethod,
    state.project?.showAverageDistance,
    state.project?.showAverageTime,
    state.project?.showMaximumDistance,
    state.project?.showMaximumTime,
    state.runConfiguration?.showDistance,
    state.runConfiguration?.showTime,
    state.runConfiguration?.showWithTimeAndDistanceColumns,
    state.templateMetadata?.closestProviderCount,
    state.isCompareMode,
    state.runConfiguration,
    isZipGapResolutionStatusEnabled,
    isZipGapAgeTrackingEnabled,
    gapFillStatusDescriptionMap
  ]);


  const bulkActionBar = useMemo(() => {
    if (!state.runConfiguration) {
      return null;
    }

    // these region set ids are for zip codes, which aren't used by report configuration
    const regionSetIds: number[] = [];
    const specialtyGroupCodes: string[] = [];

    if (state.selectedRows.length == 0) {
      regionSetIds.push(...state.regionSetIds);
      const specialtyGroupCodeFilter = state.filterSelections.specialtyGroupCode.key;
      if (specialtyGroupCodeFilter != "all")
        specialtyGroupCodes.push(specialtyGroupCodeFilter);
    }

    state.selectedRows.forEach((sr) => {
      regionSetIds.push(state.regionSetSpecialtyGroupToPartsLookup[sr].regionSetId);
      specialtyGroupCodes.push(state.regionSetSpecialtyGroupToPartsLookup[sr].specialtyGroupCode);
    });

    const selectedMemberships: number[] = [state.runConfiguration.memberSetId];
    if (state.isDualMembershipMode) {
      selectedMemberships.push(state.dualMembershipRunConfiguration.memberSetId);
    }

    const { primaryRunConfiguration, secondaryRunConfiguration } = getPrimaryAndSecondaryRunConfigurations(state.project.projectRunConfigurations, state.runConfiguration.projectRunConfigurationId);
    const { topConfig } = getTopAndBottomRunConfigurations(state.runConfiguration, state.compareToRunConfiguration, state.isCompareMode, state.isDualMembershipMode);
    const zipDetailsForExport = shouldUseComparedZipDetails(state.isDualMembershipMode, state.isCompareMode, topConfig.projectRunConfigurationType)
      ? state.zipDetailsCompared
      : state.zipDetails;

    // Mirrors ZipProjectModuleNav's showMapsTab eligibility, plus requires a single drilled-in zip + specialty.
    const showCreateMap = enabledFeatureFlags.has(Features.ZIP_CODE_PROJECT_MAPS)
      && state.isDrilledIn
      && !state.isDualMembershipMode
      && !state.isCompareMode
      && !state.runConfiguration.isTermed
      && !state.runConfiguration.isProjected
      && !state.runConfiguration.isProjectedTermed;

    return (
      <span className="bulk-action-group">
        <Link<Partial<ExportDataRouteState> & ReportCustomizationRouteState>
          to={{
            pathname: `/projects/${routeMatch.params.projectId}/adequacy/export/${state.runConfiguration?.projectRunConfigurationId}/${state.runConfiguration?.isLatestPublished}`,
            state: {
              activeModule: state.module,
              adequacyFilters: state.adequacyFilters,
              availableExportTypes: [ExportType.ZipAnalysisResultsReport, ExportType.ZipAnalysisAdequacyServicingProvider],
              comparedRunConfigurationId: state.compareToRunConfiguration?.projectRunConfigurationId,
              disableRegionFilters: false,
              disableSpecialtyGroupFilters: false,
              granularity: RegionGranularity.Zip,
              hasMarketProviders: !!state.runConfiguration.marketProviderSetId,
              hasProspectProviders: !!state.runConfiguration.prospectProviderSetId,
              initialNetworkAssessmentAnalysisType: NetworkAssessmentAnalysisType.ServiceArea,
              // values needed for export properties
              // networkTypes: state.runConfiguration && state.runConfiguration.networkTypes || undefined,
              // nextClosestProviders: showNextClosestProviders,
              perspective: ProjectPerspective.ByZipCode,
              projectResourceId: state.project.resource.resourceId,
              projectResourceName: state.project.resource.name,
              projectURL: location.pathname + location.search,
              regionSetIds,
              showAdvancedComparison: state.project.showAdvancedComparison,
              // showAppendNextClosestProviders: showNextClosestProviders,
              showExpandableSpecialties: state.hasExpandableSpecialties,
              specialtyGroupCodes,
              zipProjectSearchParameters: getSearchParametersWithPrimaryCounty(state.filterSelections, zipDetailsForExport as ZipCodeDisplayModel[]),

              // new reportcustomization
              /* oxlint-disable sort-keys */
              projectName: state.project.resource.name,
              networkAssessmentAnalysisType: NetworkAssessmentAnalysisType.ServiceArea,
              showClosestProviderToMemberReport: state.project.showClosestProviderReport,
              isZipProject: true,
              selectedRegionSetIds: regionSetIds,
              selectedSpecialties: specialtyGroupCodes,
              defaultReportType: ReportType.ZipAnalysisResultsReport,
              primaryMembership: { alias: primaryRunConfiguration.memberSetAlias || primaryRunConfiguration.memberSetName, id: primaryRunConfiguration.memberSetId },
              secondaryMembership: { alias: secondaryRunConfiguration?.memberSetAlias || secondaryRunConfiguration?.memberSetName, id: secondaryRunConfiguration?.memberSetId },
              selectedMemberships,
              selectedTimeslice: state.runConfiguration.projectRunConfigurationId, // runConfigurationId
              isCompareMode: state.isCompareMode,
              selectedComparedTimeslice: !state.isDualMembershipMode ? state.compareToRunConfiguration?.projectRunConfigurationId : null, // compareToRunConfigurationId
              isViewingOpportunity: state.adequacyFilters.opportunity,
              showAddedProviders: !!state.compareToRunConfiguration || !!state.dualMembershipRunConfiguration,
              showMatchedProviders: !!state.compareToRunConfiguration || !!state.dualMembershipRunConfiguration,
              showRemovedProviders: !!state.compareToRunConfiguration || !!state.dualMembershipRunConfiguration,
              showPatternOfCare: false,
              showRegionSummaryReport: false,
              /* oxlint-enable sort-keys */
            },
          }}
          data-testid="adequacy-view-by-all__create-report-button"
        >
          <i className="fa fa-cloud-download" /> Create Report
        </Link>
        {showCreateMap && (
          <span className="ms-3">
            <Link
              to={{ pathname: `/projects/${routeMatch.params.projectId}/${ZipProjectModuleRoutesEnum.Mapping}`, search: location.search }}
              data-testid="adequacy-view-by-all__create-map-button"
            >
              Create a Map
            </Link>
          </span>
        )}
      </span>
    );
  }, [
    location.pathname,
    location.search,
    enabledFeatureFlags,
    state.isDrilledIn,
    state.adequacyFilters,
    state.runConfiguration,
    state.project,
    state.selectedRows,
    state.regionSetSpecialtyGroupToPartsLookup,
    state.isDualMembershipMode,
    state.compareToRunConfiguration,
    state.isCompareMode,
    state.dualMembershipRunConfiguration,
    state.filterSelections,
    state.module,
    state.hasExpandableSpecialties,
    state.zipDetails,
    state.zipDetailsCompared,
  ]);

  const rowDecorator = (row: ZipCodeTableData) => {
    // row keys should always be region set specialty ids, but specialty expansions are
    // prepended with "waffles-expansion:" and don't need to be decorated
    if (isNaN(parseInt(row.key))) {
      return "";
    }

    const regionSetSpecialtyGroupParts = state.regionSetSpecialtyGroupToPartsLookup[row.key];
    const exception = (state.specialtyExceptionsMap.get(state.runConfiguration.projectRunConfigurationId) ?? {})[`${regionSetSpecialtyGroupParts?.regionSetId}:${regionSetSpecialtyGroupParts?.specialtyGroupCode}`];
    let classNames = row.metPassPercent === "Y"
      ? "success"
      : !!exception && exception.specialtyGroupExceptionStatus === SpecialtyGroupExceptionStatus.Approved && !state.isDrilledIn
        ? "info"
        : "danger";

    if (row.isInformationalSpecialty) {
      classNames += " muted";
    }
    if (row.membershipCount === "0" || row.membershipCount === "---") {
      classNames += " gray";
    }
    return classNames;
  }

  // records is always undefined here because of passThroughSorting
  const customSort = (records: ZipCodeTableData[], column: keyof ZipCodeTableData, direction: SortDirections) => {
    dispatch({
      payload: { loading: true },
      type: ZipProjectActionsEnum.Loading,
    });
    projectContext.onSortTable(column, direction);
    return records;
  }

  const buildZipCodeTableData = (
    rows: ZipCodeDisplayModel[],
    onZipOrSpecialtyGroupCodeClick?: (FilterParams: ZipCodeFilterParams) => void,
    isDrilledIn?: boolean,
    isExpansion?: boolean,
    timestamp?: string,
  ): ZipCodeTableData[] => {
    const hasInformationalSpecialties = state.zipDetails.some((z) => z.isInformationalSpecialty);
    return (rows || []).filter(row => row.regionSetSpecialtyGroupId != null).map((row) => {
      const filterParams: ZipCodeFilterParams = {
        county: row.county,
        specialtyGroupCode: row.specialtyGroupCode,
        stateName: row.stateName,
        zipCode: row.zipCode
      };

      const zipCode = isDrilledIn
        ? <>{row.zipCode}</>
        : (
          <QESButton
            qesType="secondary"
            className="p-0"
            qesTestId={`zip-view-by-all-table__row-${row.regionSetSpecialtyGroupId}--zip-code`}
            onClick={() => { onZipOrSpecialtyGroupCodeClick(filterParams) }}
          >
            {row.zipCode}
          </QESButton>
        );

      const specialtyGroupCode = isDrilledIn
        ? <Fragment key={row.regionSetSpecialtyGroupId}>{row.specialtyGroupCode} - {row.specialtyGroupDescription}</Fragment>
        : (
          <QESButton
            qesType="secondary"
            className="p-0"
            qesTestId={`zip-view-by-all-table__row-${row.regionSetSpecialtyGroupId}--specialty-group-code`}
            onClick={() => { onZipOrSpecialtyGroupCodeClick(filterParams) }}
          >
            {row.specialtyGroupCode} - {row.specialtyGroupDescription}
          </QESButton>
        );

      const exception = (state.specialtyExceptionsMap.get(state.runConfiguration.projectRunConfigurationId) ?? {})[`${row.regionSetId}:${row.specialtyGroupCode}`];
      const shouldShowExceptionHistory = !!exception
        && exception.specialtyGroupExceptionStatus
        && exception.specialtyGroupExceptionStatus !== SpecialtyGroupExceptionStatus.None
        && exception.specialtyGroupExceptionStatus !== SpecialtyGroupExceptionStatus.Archived;

      // the order here doesn't really matter because of the sortOrder properties in columnConfig
      /* oxlint-disable sort-keys */
      const newRow: ZipCodeTableData = {
        [wafflesKeyColumnName]: row.regionSetSpecialtyGroupId?.toString() ?? "",
        state: row.stateName,
        county: row.county,
        city: row.city,
        zipClass: row.zipClass,
        zipCode: isExpansion ? null : zipCode,
        isInformationalSpecialty: row.isInformationalSpecialty,
        specialty: isExpansion ? `${row.specialtyCode}` : (
          <span className="d-inline-flex align-items-end">
            <InformationalIndicator visible={hasInformationalSpecialties} isInformational={row.isInformationalSpecialty} key="info" />
            <SpecialtyGroupExceptionTimelineLink key="specialty" visible={shouldShowExceptionHistory} showHistory={shouldShowExceptionHistory} specialtyGroupException={exception} />
            {specialtyGroupCode}
          </span>
        ),
        membershipType: row.membershipType,
        minProviderCount: row.minProviderCount.toLocaleString(),
        reqDist: row.reqDist.toLocaleString(),
        reqTime: row.reqTime.toLocaleString(),
        minPassPercent: printPercentString(row.minPassPercent),
        resultsFor: timestamp,
        metDist: row.metDist ? "Y" : "N",
        metTime: row.metTime ? "Y" : "N",
        metPassPercent: row.metPassPercent ? "Y" : "N",
        // Matches the county grid: 0 when there's no open gap; specialty-level rows have no gap age.
        gapAgeDays: isExpansion ? "---" : (row.gapAgeDays ?? 0).toLocaleString(),
        membershipCount: row.membershipCount.toLocaleString(),
        memberCountWithAccess: row.memberCountWithAccess?.toLocaleString(),
        withAccessPercent: printPercentString(row.withAccessPercent),
        memberCountWithoutAccess: row.memberCountWithoutAccess?.toLocaleString(),
        withoutAccessPercent: printPercentString(row.withoutAccessPercent),
        avgDist: printNumericString(row.averageDistance),
        avgDistWith: printNumericString(row.averageDistanceWithAccess),
        avgDistWithout: printNumericString(row.averageDistanceWithoutAccess),
        maxDist: printNumericString(row.maxDistance),
        maxDistWith: printNumericString(row.maxDistanceWithAccess),
        maxDistWithout: printNumericString(row.maxDistanceWithoutAccess),
        withDistPercent: printPercentString(row.withDistPercent),
        avgTime: printNumericString(row.averageTime),
        avgTimeWith: printNumericString(row.averageTimeWithAccess),
        avgTimeWithout: printNumericString(row.averageTimeWithoutAccess),
        maxTime: printNumericString(row.maxTime),
        maxTimeWith: printNumericString(row.maxTimeWithAccess),
        maxTimeWithout: printNumericString(row.maxTimeWithoutAccess),
        withTimePercent: printPercentString(row.withTimePercent),
        avgDist2: printNumericString(row.averageDistance2),
        avgDistWith2: printNumericString(row.averageDistanceWithAccess2),
        avgDistWithout2: printNumericString(row.averageDistanceWithoutAccess2),
        maxDist2: printNumericString(row.maxDistance2),
        maxDistWith2: printNumericString(row.maxDistanceWithAccess2),
        maxDistWithout2: printNumericString(row.maxDistanceWithoutAccess2),
        avgTime2: printNumericString(row.averageTime2),
        avgTimeWith2: printNumericString(row.averageTimeWithAccess2),
        avgTimeWithout2: printNumericString(row.averageTimeWithoutAccess2),
        maxTime2: printNumericString(row.maxTime2),
        maxTimeWith2: printNumericString(row.maxTimeWithAccess2),
        maxTimeWithout2: printNumericString(row.maxTimeWithoutAccess2),
        accessTelehealthCredit: printPercentString(row.accessTelehealthCredit),
        providerCount: row.providerCount.toLocaleString(),
        inZipProviderCount: printNumericString(row.inZipProviderCount, 0),
        marketAccessPercent: printPercentString(row.marketAccessPercent),
        marketPercent: printPercentString(row.marketPercent),
        marketServicing: row.marketServicing?.toLocaleString(),
        inZipMarketProviderCount: printNumericString(row.inZipMarketProviderCount, 0),
        servicingProviderToMemberRatio: row.servicingProviderToMemberRatio?.toLocaleString() || "---",
        gapFillStatus: row.gapFillStatus ? gapFillStatusDescriptionMap[row.gapFillStatus] : "---",
        gapFillStatusUpdatedDate: row.gapFillStatusUpdatedDate
          ? formatDateCustom(row.gapFillStatusUpdatedDate, "MMM D 'YY hh:mm A")
          : "---",
        gapFillStatusUpdatedByName: row.gapFillStatusUpdatedByName || "---"
      }
      return newRow;
    });
  }

  const [tableData, tableDataCompared] = useMemo(() => {
    const { topConfig, bottomConfig } = getTopAndBottomRunConfigurations(state.runConfiguration, state.compareToRunConfiguration, state.isCompareMode, state.isDualMembershipMode);
    const topTimestamp = enabledFeatureFlags.has(Features.ZIP_CODE_ANALYSIS_TARGETING) || enabledFeatureFlags.has(Features.ZIP_CODE_ANALYSIS_TERMINATION) ? getResultsForLabel(topConfig) : formatDateTime(topConfig?.dateCreated);
    const bottomTimestamp = enabledFeatureFlags.has(Features.ZIP_CODE_ANALYSIS_TARGETING) || enabledFeatureFlags.has(Features.ZIP_CODE_ANALYSIS_TERMINATION) ? getResultsForLabel(bottomConfig) : formatDateTime(bottomConfig?.dateCreated);

    const usedDetails = shouldUseComparedZipDetails(state.isDualMembershipMode, state.isCompareMode, topConfig.projectRunConfigurationType)
      ? state.zipDetailsCompared
      : state.zipDetails;
    
    const newTableData = buildZipCodeTableData(usedDetails as ZipCodeDisplayModel[], projectContext.onClickSpecialtyGroupOrZipCode, state.isDrilledIn, false, topTimestamp);
    const newTableDataCompared = state.isCompareMode || state.isDualMembershipMode ? buildZipCodeTableData(state.zipDetailsCompared as ZipCodeDisplayModel[], projectContext.onClickSpecialtyGroupOrZipCode, state.isDrilledIn, false, bottomTimestamp) : undefined;

    const [completeNewTableData, completeNewTableDataCompared] = fillInMissingCompareModeRows(newTableData, newTableDataCompared, blankZipCodeRow, topTimestamp, bottomTimestamp);
    return [completeNewTableData, completeNewTableDataCompared];
  }, [state.zipDetails, state.zipDetailsCompared, state.isCompareMode, state.skip, state.isDualMembershipMode, state.runConfiguration, state.specialtyExceptionsMap, gapFillStatusDescriptionMap]);

  const expansionData = useMemo(() => {
    // Expansions must come from the same run configuration as the parent rows above.
    const { topConfig } = getTopAndBottomRunConfigurations(state.runConfiguration, state.compareToRunConfiguration, state.isCompareMode, state.isDualMembershipMode);
    const usedExpansions = shouldUseComparedZipDetails(state.isDualMembershipMode, state.isCompareMode, topConfig.projectRunConfigurationType)
      ? state.zipExpansionsCompared
      : state.zipExpansions;

    const expansionData = new Map<string, ZipCodeTableData[]>();
    usedExpansions?.forEach((values, key) => {
      expansionData.set(key, buildZipCodeTableData(values as ZipCodeDisplayModel[], () => null, false, true));
    })
    return expansionData;
  }, [state.zipExpansions, state.zipExpansionsCompared, state.isCompareMode, state.isDualMembershipMode, state.runConfiguration, state.compareToRunConfiguration]);

  const comparedExpansionData = useMemo(() => {
    if (!state.isCompareMode)
      return undefined;

    const expansionData = new Map<string, ZipCodeTableData[]>();
    state.zipExpansionsCompared.forEach((values, key) => {
      expansionData.set(key, buildZipCodeTableData(values as ZipCodeDisplayModel[], () => null, false, true));
    })
    return expansionData;
  }, [state.zipExpansionsCompared, state.isCompareMode]);

  const columnGroups: ColumnGroup<ZipCodeTableData>[] = useMemo(() => {
    return [
      {
        children: [
          "state",
          "county",
          "city",
          "zipClass",
          "zipCode",
          "specialty",
          "resultsFor",
          "membershipType",
          "membershipCount",
        ],
        hideKey: true,
        key: "general",
        testId: "general",
      },
      {
        children: [
          "reqDist",
          "reqTime",
          "minPassPercent",
          "metDist",
          "metTime",
          "metPassPercent",
          "gapAgeDays",
          "memberCountWithAccess",
          "withAccessPercent",
          "memberCountWithoutAccess",
          "withoutAccessPercent",
          "minProviderCount",
          "avgDist",
          "avgDistWith",
          "avgDistWithout",
          "maxDist",
          "maxDistWith",
          "maxDistWithout",
          "withDistPercent",
          "avgTime",
          "avgTimeWith",
          "avgTimeWithout",
          "maxTime",
          "maxTimeWith",
          "maxTimeWithout",
          "withTimePercent",
          "avgDist2",
          "avgDistWith2",
          "avgDistWithout2",
          "maxDist2",
          "maxDistWith2",
          "maxDistWithout2",
          "avgTime2",
          "avgTimeWith2",
          "avgTimeWithout2",
          "maxTime2",
          "maxTimeWith2",
          "maxTimeWithout2",
          "accessTelehealthCredit",
          "providerCount",
          "inZipProviderCount",
          "servicingProviderToMemberRatio",
        ],
        key: "Access Requirements",
        testId: "access-requirements",
      },
      {
        children: ["marketAccessPercent", "marketServicing", "inZipMarketProviderCount", "marketPercent"],
        hideKey: !displayMarketAnalysisResults,
        key: "Market Analysis",
        showInfoIcon: true,
        testId: "market-analysis",
        tooltip: getMarketAnalysisTooltip(state.runConfiguration),
      },
      {
        children: ["gapFillStatus", "gapFillStatusUpdatedDate", "gapFillStatusUpdatedByName"],
        hideKey: !isZipGapResolutionStatusEnabled,
        key: "Gap Resolution",
        testId: "gap-resolution",
      }
    ];
  }, [state.runConfiguration, isZipGapResolutionStatusEnabled, isZipGapAgeTrackingEnabled, gapFillStatusDescriptionMap]);

  return (
    <Waffles<ZipCodeTableData>
      key={String(state.sortResetCount)}
      bulkActionBar={bulkActionBar}
      columns={columnConfig}
      columnGroups={columnGroups}
      compareData={tableDataCompared}
      compareExpansions={comparedExpansionData}
      compareModeKeyColumns={6}
      customSort={customSort}
      data={tableData}
      dataExpansions={expansionData}
      enablePagination={true}
      fullWidth={true}
      handleCheckboxChange={handleCheckboxChange}
      loading={state.loading}
      paginationProps={paginationProps}
      passThroughSorting={true}
      rowDecorator={rowDecorator}
      selectedKeys={state.selectedRows}
      totalNumberOfRecords={calculatedTotalNumberOfRecords}
      testId="zip-view-by-all-table"
    />
  );
}